import { Flex, Modal, Typography, message } from 'antd';
import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomButton } from '@/components/ui';
import { denyReschedule } from '@/api/booking';

import { notifyActionError } from '@/helpers/subscriptionError';
interface KeepFeeProps {
  open: boolean;
  onCancel: () => void;
  bookingId: string;
  fee: string;
  franchiseName?: string;
}

// Consultant-facing confirmation for keeping the late-cancellation fee on a
// franchisor's reschedule request. "KEEP FEE & CHARGE" denies the reschedule
// (charging the fee); "BACK" backs out without touching the booking.
export const KeepFee = (props: KeepFeeProps) => {
  const { bookingId } = props;
  // Guard against callers that forgot to pass `fee` — never render literal
  // "undefined" in the copy below.
  const fee = props.fee ?? '$0';
  const franchiseName = props.franchiseName ?? 'The franchisor';
  const queryClient = useQueryClient();

  const keepFee = useMutation({
    mutationKey: ['deny-reschedule', bookingId],
    mutationFn: () => denyReschedule(bookingId),
    onSuccess: () => {
      message.success('Late-cancellation fee kept.');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
      props.onCancel();
    },
    // TCX-900: a Stripe outage must not read as a rejected action.
    onError: (error) => notifyActionError(error),
  });

  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      width={640}
      centered
      footer={null}
      closeIcon={<CloseOutlined style={{ color: '#000' }} />}
      styles={{ content: { borderRadius: 20 }, body: { padding: '18px 18px' } }}
    >
      <Flex vertical align={"center"} gap={16}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(246, 181, 30, 0.14)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WarningOutlined style={{ fontSize: 24, color: '#9a6b00' }} />
        </div>
        <Typography.Text style={{ fontSize: 22, fontWeight: 700, textAlign: 'center' }}>
          Keep the late-cancellation fee?
        </Typography.Text>
        <Typography.Text type={"secondary"} style={{ textAlign: 'center' }}>
          The rescheduled meeting still goes on your calendar at the new time. Keeping the fee
          means the waiver is not applied —{' '}
          <Typography.Text strong>
            {franchiseName} will be charged {fee}
          </Typography.Text>{' '}
          and notified of your decision.
        </Typography.Text>
      </Flex>
      <Flex vertical gap={12} style={{ marginTop: 24 }}>
        <CustomButton
          type={"primary"}
          text={`KEEP FEE & CHARGE ${fee}`}
          loading={keepFee.isPending}
          onClick={() => keepFee.mutate()}
          style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
        />
        <CustomButton type={"default"} text={"BACK"} onClick={props.onCancel} />
      </Flex>
    </Modal>
  );
};
